import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';

// Services
import stylistService from '../../services/stylistService';
import * as locationService from '../../services/locationService';

// Redux
import { useAppSelector } from '../../hooks/useRedux';

// Types
import { Stylist } from '../../types/stylist';
import { publicStylistRef } from '../../utils/publicRefs';

// Components
import StylistCard from '../../components/client/StylistCard';
import { useIsSignedIn, useMarketplacePaths, useRequireSignIn } from '../../utils/guestAccess';
import TeslaButton from '../../components/common/TeslaButton';
import ScrollReveal from '../../components/common/ScrollReveal';
import FilterDrawer from '../../components/client/FilterDrawer';
import SearchHeader from '../../components/client/SearchHeader';
import LocationSelector from '../../components/client/LocationSelector';
import CategorySelector from '../../components/client/CategorySelector';
import ToastNotification from '../../components/common/ToastNotification';
import Skeleton from '../../components/common/Skeleton';

// Constants
import { categories, distanceOptions } from '../../constants/constants';
import { areCoordinatesSignificantlyDifferent } from '../../constants/locationConstants';

const BrowseStylesPage: React.FC = () => {
  const navigate = useNavigate();
  // Use Redux instead of AuthContext to sync with App.tsx user restoration
  const { user, token, isLoading: authLoading } = useAppSelector((state) => state.auth);
  // Guests (no session) browse via /browse; favorites need an account.
  const isSignedIn = useIsSignedIn();
  const paths = useMarketplacePaths();
  const requireSignIn = useRequireSignIn();

  // State variables
  const [stylists, setStylists] = useState<Stylist[]>([]);
  const [filteredStylists, setFilteredStylists] = useState<Stylist[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDistance, setSelectedDistance] = useState('any');
  const [showOnlyNearby, setShowOnlyNearby] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isDistanceFilterOpen, setIsDistanceFilterOpen] = useState(false);
  const [favoriteStylists, setFavoriteStylists] = useState<{ [key: string]: boolean }>({});
  const [stylistDistances, setStylistDistances] = useState<{ [key: string]: string | null }>({});
  const [nearbyStylistIds, setNearbyStylistIds] = useState<{ [key: string]: boolean }>({});
  const [locationScores, setLocationScores] = useState<{ [key: string]: number }>({});
  const [usingCurrentLocation, setUsingCurrentLocation] = useState(false);
  const [currentLocationData, setCurrentLocationData] = useState<any>(null);
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [toast, setToast] = useState<{ show: boolean, message: string, type: 'success' | 'error' }>({
    show: false,
    message: '',
    type: 'success'
  });

  // Show success toast
  const showSuccess = (message: string) => {
    setToast({ show: true, message, type: 'success' });
    setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
  };

  // Show error toast
  const showError = (message: string) => {
    setToast({ show: true, message, type: 'error' });
    setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
  };

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Get current location
  const getCurrentLocation = async () => {
    try {
      setLoadingLocation(true);

      // First, try to get coordinates (this usually works even if reverse geocoding fails)
      const coordinates = await locationService.getCurrentLocation();
      
      if (!coordinates) {
        // Can't get coordinates - try stored location as fallback
        if (user?.location?.coordinates?.coordinates && user?.location?.city && user?.location?.state) {
          console.log('Using stored user location as fallback (no coordinates available)');
          const formattedLocation = {
            coordinates: user.location.coordinates,
            city: user.location.city,
            state: user.location.state,
            zipCode: user.location.zipCode || '',
          };

          setCurrentLocationData(formattedLocation);
          setUsingCurrentLocation(true);
          await fetchStylists(true);
          showSuccess(`Now showing stylists near ${formattedLocation.city}, ${formattedLocation.state} (stored location)`);
          setLoadingLocation(false);
          return;
        }

        // No coordinates and no stored location
        showError(isSignedIn
          ? 'Unable to get your location. Please check your browser settings or use "My Address" instead.'
          : 'Unable to get your location. Showing stylists in all areas.');
        setLoadingLocation(false);
        return;
      }

      // We have coordinates! Now try to get address (but don't fail if this doesn't work)
      const locationDetails = await locationService.getFullLocationDetails();
      
      // Format location - use address if available, otherwise just coordinates
      let formattedLocation: any = {
        coordinates: locationService.formatCoordinatesForMongoDB(coordinates),
        city: '',
        state: '',
        zipCode: '',
      };

      let locationMessage = '';

      if (locationDetails?.address?.city && locationDetails?.address?.state) {
        // We have both coordinates and address - perfect!
        formattedLocation.city = locationDetails.address.city;
        formattedLocation.state = locationDetails.address.state;
        formattedLocation.zipCode = locationDetails.address.zipCode || '';
        locationMessage = `Now showing stylists near ${formattedLocation.city}, ${formattedLocation.state}`;
      } else {
        // We have coordinates but no address - still use coordinates for accurate search
        // Check if stored location is significantly different using distance calculation
        const storedCoords = user?.location?.coordinates?.coordinates;
        const currentCoords = formattedLocation.coordinates.coordinates;
        
        if (storedCoords && 
            Array.isArray(storedCoords) && storedCoords.length === 2 &&
            Array.isArray(currentCoords) && currentCoords.length === 2) {
          // Use Haversine formula to calculate actual distance
          const isDifferent = areCoordinatesSignificantlyDifferent(
            storedCoords as [number, number],
            currentCoords as [number, number]
          );
          
          if (isDifferent) {
            // Coordinates are significantly different (>1km) - use current coordinates
            locationMessage = 'Using your current location (coordinates)';
          } else if (user?.location?.city && user?.location?.state) {
            // Coordinates are similar, use stored address for display
            formattedLocation.city = user.location.city;
            formattedLocation.state = user.location.state;
            formattedLocation.zipCode = user.location.zipCode || '';
            locationMessage = `Now showing stylists near ${formattedLocation.city}, ${formattedLocation.state}`;
          } else {
            // No stored address - just use coordinates
            locationMessage = 'Using your current location';
          }
        } else if (user?.location?.city && user?.location?.state) {
          // Can't compare coordinates, use stored address if available
          formattedLocation.city = user.location.city;
          formattedLocation.state = user.location.state;
          formattedLocation.zipCode = user.location.zipCode || '';
          locationMessage = `Now showing stylists near ${formattedLocation.city}, ${formattedLocation.state}`;
        } else {
          // No stored address - just use coordinates
          locationMessage = 'Using your current location';
        }
      }

      setCurrentLocationData(formattedLocation);
      setUsingCurrentLocation(true);

      // Refresh the stylists list with new location
      await fetchStylists(true);

      showSuccess(locationMessage);
    } catch (error) {
      console.warn('Error getting current location, trying fallback:', error);
      
      // Try fallback to stored location
      if (user?.location?.coordinates?.coordinates && user?.location?.city && user?.location?.state) {
        console.log('Using stored user location as fallback after error');
        const formattedLocation = {
          coordinates: user.location.coordinates,
          city: user.location.city,
          state: user.location.state,
          zipCode: user.location.zipCode || '',
        };

        setCurrentLocationData(formattedLocation);
        setUsingCurrentLocation(true);
        await fetchStylists(true);
        showSuccess(`Now showing stylists near ${formattedLocation.city}, ${formattedLocation.state} (stored location)`);
      } else {
        showError(isSignedIn
          ? 'Failed to get your location. Please try again or use "My Address".'
          : 'Failed to get your location. Showing stylists in all areas.');
      }
    } finally {
      setLoadingLocation(false);
    }
  };

  // Fetch favorite statuses
  const fetchFavoriteStatuses = useCallback(async (stylistIds: string[]) => {
    try {
      if (!stylistIds.length || !isSignedIn) return;

      const statuses: { [key: string]: boolean } = {};

      const promises = stylistIds.map(async (id) => {
        try {
          const status = await stylistService.checkFavorite(id);
          statuses[id] = status;
        } catch (error) {
          console.log(`Could not check favorite for stylist ${id}`);
          statuses[id] = false;
        }
      });

      await Promise.all(promises);

      setFavoriteStylists(prev => ({ ...prev, ...statuses }));
    } catch (error) {
      console.error('Error fetching favorite statuses:', error);
    }
  }, [isSignedIn]);

  // Fetch stylists from API (using enhanced backend fields)
  const fetchStylists = useCallback(async (shouldRefresh = false) => {
    console.log('🔍 [fetchStylists] Called with authLoading:', authLoading);
    
    // Don't fetch if auth is still loading
    if (authLoading) {
      console.log('⏳ [fetchStylists] Skipping - auth still loading');
      return;
    }

    console.log('✅ [fetchStylists] Auth loaded, proceeding...');
    console.log('👤 [fetchStylists] User object:', user);
    console.log('📍 [fetchStylists] User location:', user?.location);
    
    // If user is null but auth is loaded, try to get user from localStorage as fallback
    let userToUse = user;
    // Cached profile fallback is for a session whose profile is still loading —
    // never for a guest, who must not inherit a previous user's saved address.
    if (!userToUse && !authLoading && token) {
      try {
        const storedUser = localStorage.getItem('user');
        console.log('🔍 [fetchStylists] Checking localStorage for user:', {
          hasStoredUser: !!storedUser,
          storedUserLength: storedUser?.length
        });
        if (storedUser) {
          userToUse = JSON.parse(storedUser);
          console.log('📦 [fetchStylists] Using user from localStorage as fallback:', {
            name: userToUse?.name,
            hasLocation: !!userToUse?.location,
            hasCoordinates: !!userToUse?.location?.coordinates?.coordinates,
            coordinates: userToUse?.location?.coordinates?.coordinates
          });
        } else {
          console.log('⚠️ [fetchStylists] No user found in localStorage');
        }
      } catch (e) {
        console.warn('⚠️ [fetchStylists] Failed to parse stored user:', e);
      }
    }
    
    setLoading(true);
    try {
      // pick which location to use
      const loc = usingCurrentLocation && currentLocationData
        ? currentLocationData
        : userToUse?.location || null;
  
      console.log('📌 [fetchStylists] Selected location:', loc);
      console.log('🗺️ [fetchStylists] Coordinates object:', loc?.coordinates);
      console.log('📐 [fetchStylists] Coordinates array:', loc?.coordinates?.coordinates);
  
      // build your params object
      let params: any = {};
      if (loc) {
        params.city    = loc.city;
        params.state   = loc.state;
        params.zipCode = loc.zipCode;
        if (selectedDistance !== 'any') {
          params.maxDistance = +selectedDistance;
        }
        if (loc.coordinates?.coordinates?.length === 2) {
          // API expects latitude & longitude separately
          params.latitude  = loc.coordinates.coordinates[1];
          params.longitude = loc.coordinates.coordinates[0];
          console.log('✅ [fetchStylists] Coordinates extracted:', { lat: params.latitude, lon: params.longitude });
        } else {
          console.log('❌ [fetchStylists] No valid coordinates found');
          console.log('   - Has coordinates?', !!loc.coordinates);
          console.log('   - Has coordinates.coordinates?', !!loc.coordinates?.coordinates);
          console.log('   - Array length:', loc.coordinates?.coordinates?.length);
        }
      } else {
        console.log('❌ [fetchStylists] No location available');
      }
      
      console.log('📦 [fetchStylists] Final API params:', params);
  
      // call the service — backend now returns enhanced fields
      const stylistData: any[] = await stylistService.getStylists({
        userType: 'stylist',
        page: 1,
        limit: 10,
        search: debouncedSearch || undefined,
        serviceCategory:
          selectedCategory !== 'all'
            ? selectedCategory.toLowerCase()
            : undefined,
        ...params,
      });
  
      console.log('📥 [fetchStylists] Received stylists from API:', stylistData.length);
      if (stylistData.length > 0) {
        console.log('🔍 [fetchStylists] First stylist sample:', {
          id: stylistData[0]._id,
          name: stylistData[0].name,
          distanceFormatted: stylistData[0].distanceFormatted,
          canServeYou: stylistData[0].canServeYou,
          smartScore: stylistData[0].smartScore,
          hasDistance: !!stylistData[0].distanceFormatted,
          hasCanServe: typeof stylistData[0].canServeYou !== 'undefined',
          hasSmartScore: typeof stylistData[0].smartScore !== 'undefined',
        });
      }
  
      // Extract enhanced fields from backend response
      const newDistances: { [key: string]: string | null } = {};
      const newNearbyIds: { [key: string]: boolean } = {};
      const newScores: { [key: string]: number } = {};
  
      stylistData.forEach(stylist => {
        // Use backend-calculated fields
        newDistances[stylist._id] = stylist.distanceFormatted || null;
        newNearbyIds[stylist._id] = stylist.canServeYou || false;
        newScores[stylist._id] = stylist.smartScore || 0;
      });
  
      console.log('📊 [fetchStylists] Extracted enhanced fields:', {
        distances: Object.keys(newDistances).length,
        nearbyIds: Object.keys(newNearbyIds).length,
        scores: Object.keys(newScores).length,
        sampleDistance: newDistances[stylistData[0]?._id] || 'N/A',
        sampleNearby: newNearbyIds[stylistData[0]?._id] || 'N/A',
        sampleScore: newScores[stylistData[0]?._id] || 'N/A',
      });
  
      setStylistDistances((prev) => ({ ...prev, ...newDistances }));
      setNearbyStylistIds((prev) => ({ ...prev, ...newNearbyIds }));
      setLocationScores((prev) => ({ ...prev, ...newScores }));
  
      // Store all stylists (filtering will be handled by useEffect based on showOnlyNearby toggle)
      setStylists(stylistData);
      // The useEffect will handle filtering based on showOnlyNearby state
      await fetchFavoriteStatuses(stylistData.map((s) => s._id));
    } catch (err) {
      console.error(err);
      showError('Failed to load stylists');
    } finally {
      setLoading(false);
    }
  }, [
    authLoading,
    user,
    token,
    usingCurrentLocation,
    currentLocationData,
    debouncedSearch,
    selectedCategory,
    selectedDistance,
    fetchFavoriteStatuses,
  ]);
  

  // Track if we've done initial fetch to avoid duplicate calls
  const [hasInitialFetch, setHasInitialFetch] = React.useState(false);

  // Apply filters when category, distance, or nearby toggle changes
  useEffect(() => {
    // Only fetch if auth is loaded
    if (!authLoading) {
      fetchStylists(true);
      setHasInitialFetch(true);
    }
  }, [fetchStylists, authLoading]);
  
  // Refetch when user loads with location (one-time refetch after initial load)
  const prevUserIdRef = React.useRef<string | null>(user?._id || null);
  const hasRefetchedForUserRef = React.useRef(false);
  
  useEffect(() => {
    const currentUserId = user?._id || null;
    const userJustLoaded = prevUserIdRef.current === null && currentUserId !== null;
    const userHasLocation = user?.location?.coordinates?.coordinates?.length === 2;
    
    console.log('👀 [BrowseStyles] User effect check:', {
      prevUserId: prevUserIdRef.current,
      currentUserId,
      userJustLoaded,
      userHasLocation,
      authLoading,
      hasInitialFetch,
      usingCurrentLocation,
      hasRefetched: hasRefetchedForUserRef.current
    });
    
    if (!authLoading && hasInitialFetch && userJustLoaded && userHasLocation && !usingCurrentLocation && !hasRefetchedForUserRef.current) {
      console.log('🔄 [BrowseStyles] User loaded with location, refetching stylists with coordinates...');
      hasRefetchedForUserRef.current = true;
      // Use a small delay to ensure state is fully updated
      const timer = setTimeout(() => {
        fetchStylists(true);
      }, 200);
      prevUserIdRef.current = currentUserId;
      return () => clearTimeout(timer);
    }
    
    // Update ref for next check
    if (currentUserId !== prevUserIdRef.current) {
      prevUserIdRef.current = currentUserId;
      // Reset refetch flag if user changed
      if (currentUserId !== null) {
        hasRefetchedForUserRef.current = false;
      }
    }
  }, [user?._id, user?.location?.coordinates?.coordinates?.length, authLoading, hasInitialFetch, usingCurrentLocation, fetchStylists]);

  // Filter stylists when showOnlyNearby toggle changes
  useEffect(() => {
    if (showOnlyNearby) {
      // Filter to only show stylists that can serve the user
      const nearby = stylists.filter(stylist => nearbyStylistIds[stylist._id] === true);
      setFilteredStylists(nearby);
    } else {
      // Show all stylists
      setFilteredStylists(stylists);
    }
  }, [showOnlyNearby, stylists, nearbyStylistIds]);

  // Handle search input — server applies `search` (name + short bio); debounced via `debouncedSearch`.
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  // Handle stylist click - navigate to the stylist details page
  const handleStylistClick = (stylist: Stylist) => {
    navigate(paths.stylist(publicStylistRef(stylist)), { 
      state: { stylist } 
    });
  };

  // Handle favorite toggle
  const handleFavoriteToggle = async (stylistId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    if (!isSignedIn) {
      requireSignIn('favorites');
      return;
    }
    try {
      const isFavorite = favoriteStylists[stylistId];

      if (isFavorite) {
        await stylistService.removeFavorite(stylistId);
        setFavoriteStylists(prev => ({ ...prev, [stylistId]: false }));
        showSuccess('Removed from favorites');
      } else {
        await stylistService.addFavorite(stylistId);
        setFavoriteStylists(prev => ({ ...prev, [stylistId]: true }));
        showSuccess('Added to favorites');
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      showError('Failed to update favorites');
    }
  };

  // Toggle filter drawer
  const toggleFilterDrawer = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  // Toggle distance filter
  const toggleDistanceFilter = () => {
    setIsDistanceFilterOpen(!isDistanceFilterOpen);
  };

  return (
    <ScrollReveal animation="fade-in-up">
      <PageContainer>
        {/* Toast notification */}
        {toast.show && (
          <ToastNotification
            message={toast.message}
            type={toast.type}
          />
        )}

        {/* Header with search */}
        <SearchHeader
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          onFilterOpen={toggleFilterDrawer}
        />

        {/* Location selector */}
        <LocationSelector
          addressLabel={isSignedIn ? 'My Address' : 'All Areas'}
          usingCurrentLocation={usingCurrentLocation}
          currentLocationData={currentLocationData}
          loadingLocation={loadingLocation}
          onLocationTypeChange={(useCurrentLocation) => {
            setUsingCurrentLocation(useCurrentLocation);
            if (useCurrentLocation) {
              getCurrentLocation();
            } else {
              fetchStylists(true);
            }
          }}
          onGetCurrentLocation={getCurrentLocation}
        />

        {/* Filter toggles */}
        <FilterToggleSection>
          <NearbyToggle>
            <ToggleLabel>Show only nearby stylists</ToggleLabel>
            <Toggle
              isActive={showOnlyNearby}
              onClick={() => setShowOnlyNearby(!showOnlyNearby)}
            />
          </NearbyToggle>

          <DistanceFilterButton onClick={toggleDistanceFilter}>
            <FilterIcon>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 17v2h6v-2H3zM3 5v2h10V5H3zm10 16v-2h8v-2h-8v-2h-2v6h2zM7 9v2H3v2h4v2h2V9H7zm14 4v-2H11v2h10zm-6-4h2V7h4V5h-4V3h-2v6z" />
              </svg>
            </FilterIcon>
            <span>Distance</span>
          </DistanceFilterButton>
        </FilterToggleSection>

        {/* Distance filter dropdown */}
        {isDistanceFilterOpen && (
          <DistanceFilterContainer>
            <DistanceOptions>
              {distanceOptions.map((option) => (
                <DistanceOption
                  key={option.id}
                  isSelected={selectedDistance === option.id}
                  onClick={() => setSelectedDistance(option.id)}
                >
                  {option.label}
                </DistanceOption>
              ))}
            </DistanceOptions>
          </DistanceFilterContainer>
        )}

        {/* Categories section */}
        <CategorySelector
          selectedCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
          categories={categories}
        />

        {/* Stylist grid */}
        <StylistsSection>
          {loading ? (
            /* Loading skeleton state */
            <StylistGrid>
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="skeleton-card">
                  <Skeleton height="200px" width="100%" />
                  <div style={{ padding: '16px' }}>
                    <Skeleton height="24px" width="70%" marginBottom="8px" />
                    <Skeleton height="16px" width="40%" marginBottom="8px" />
                    <Skeleton height="16px" width="60%" marginBottom="16px" />
                    <Skeleton height="36px" width="100%" />
                  </div>
                </div>
              ))}
            </StylistGrid>
          ) : filteredStylists.length > 0 ? (
            /* Stylists grid */
            <StylistGrid>
              {filteredStylists.map((stylist) => (
                <motion.div
                  key={stylist._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <StylistCard
                    stylist={stylist}
                    isFavorite={favoriteStylists[stylist._id] || false}
                    onClick={() => handleStylistClick(stylist)}
                    onFavoriteToggle={(e) => handleFavoriteToggle(stylist._id, e)}
                    // Additional props for enhanced display
                    isNearby={nearbyStylistIds[stylist._id] || false}
                    distanceLabel={stylistDistances[stylist._id] || null}
                    smartScore={locationScores[stylist._id]}
                  />
                </motion.div>
              ))}
            </StylistGrid>
          ) : (
            /* Empty state */
            <EmptyState>
              <EmptyStateIcon>
                <svg width="64" height="64" viewBox="0 0 24 24" fill="#666">
                  <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
                </svg>
              </EmptyStateIcon>
              <EmptyStateTitle>
                {searchQuery
                  ? 'No stylists found for your search'
                  : showOnlyNearby
                    ? 'No nearby stylists available'
                    : 'No stylists available at the moment'}
              </EmptyStateTitle>
              {showOnlyNearby && (
                <TeslaButton
                  variant="primary"
                  onClick={() => setShowOnlyNearby(false)}
                >
                  Show all stylists
                </TeslaButton>
              )}
            </EmptyState>
          )}
        </StylistsSection>

        {/* Mobile filter drawer */}
        <FilterDrawer
          isOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          filters={{
            specialties: [],
            maxDistance: parseInt(selectedDistance === 'any' ? '50' : selectedDistance),
            minRating: 0,
            priceRange: [0, 500],
            availability: '',
            sortBy: 'recommended',
            showOnlyNearby
          }}
          onFilterChange={filters => {
            if (filters.maxDistance) {
              setSelectedDistance(String(filters.maxDistance));
            }
            setShowOnlyNearby(!!filters.showOnlyNearby);
          }}
          onApply={() => setIsFilterOpen(false)}
        />
      </PageContainer>
    </ScrollReveal>
  );
};

// Styled components
const PageContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px 60px;
  
  @media (max-width: 768px) {
    padding: 0 16px 40px;
  }
`;

const FilterToggleSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #fff;
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

const NearbyToggle = styled.div`
  display: flex;
  align-items: center;
`;

const ToggleLabel = styled.span`
  font-size: 16px;
  font-weight: 500;
  color: #171a20;
  margin-right: 16px;
`;

const Toggle = styled.button<{ isActive: boolean }>`
  position: relative;
  width: 46px;
  height: 24px;
  border-radius: 12px;
  background-color: ${props => props.isActive ? '#C9A84C' : '#ccc'};
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${props => props.isActive ? '24px' : '2px'};
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: white;
    transition: left 0.2s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }
`;

const DistanceFilterButton = styled.button`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  background-color: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 40px;
  font-size: 14px;
  font-weight: 500;
  color: #171a20;
  cursor: pointer;
  transition: all 0.15s ease;
  
  &:hover {
    background-color: #ebebeb;
  }
`;

const FilterIcon = styled.span`
  margin-right: 8px;
  display: flex;
  align-items: center;
`;

const DistanceFilterContainer = styled.div`
  background-color: #fff;
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

const DistanceOptions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const DistanceOption = styled.button<{ isSelected: boolean }>`
  padding: 8px 16px;
  background-color: ${props => props.isSelected ? '#C9A84C' : '#f5f5f5'};
  color: ${props => props.isSelected ? '#fff' : '#171a20'};
  border: 1px solid ${props => props.isSelected ? '#C9A84C' : '#e0e0e0'};
  border-radius: 40px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  
  &:hover {
    background-color: ${props => props.isSelected ? '#C9A84C' : '#ebebeb'};
  }
`;

const StylistsSection = styled.section`
  padding: 16px 0;
`;

const StylistGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  
  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
    max-width: 560px;
    margin-left: auto;
    margin-right: auto;
  }
  
  @media (max-width: 768px) {
    gap: 16px;
    max-width: 100%;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

const EmptyStateIcon = styled.div`
  margin-bottom: 16px;
  opacity: 0.7;
`;

const EmptyStateTitle = styled.h3`
  font-size: 18px;
  font-weight: 500;
  color: #666;
  margin: 0 0 24px;
`;

export default BrowseStylesPage;